import { escapeRegExp } from 'lodash';

import type {
  ChordShortcut,
  ComboShortcut,
  KeyboardShortcut,
  SequenceShortcut,
} from '@neptune/shared/keyboard-domain';

export const stringToKeyboardShortcut = (shortcut: string): KeyboardShortcut => {
  const sequenceSeparated = shortcut.split('>');

  if (sequenceSeparated.length === 1) {
    // Not a sequence nor combo
    return {
      type: 'chord',
      shortcut: new Set(shortcut.split('+')),
    };
  }

  if (shortcut.length > sequenceSeparated.length * 2 - 1) {
    return {
      type: 'combo',
      shortcut: sequenceSeparated.map((part) => new Set(part.split('+'))),
    };
  }

  return {
    type: 'sequence',
    shortcut: sequenceSeparated,
  };
};

export const getChordShortcutIfMatch = (
  registeredChords: Map<string, ChordShortcut>,
  chordToCheck: ChordShortcut['shortcut'],
) => {
  for (const [shortcut, chord] of registeredChords.entries()) {
    // Chords should match 1 to 1
    if (chord.shortcut.symmetricDifference(chordToCheck).size === 0) {
      return shortcut;
    }
  }

  return null;
};

export const getSequenceShortcutIfMatch = (
  registeredSequences: Map<string, SequenceShortcut>,
  sequenceToCheck: SequenceShortcut['shortcut'],
) => {
  for (const [shortcut, seq] of registeredSequences.entries()) {
    const currStr = sequenceToCheck.join('|');
    const seqStr = seq.shortcut.join('|');

    if (new RegExp(`(^|[|])${escapeRegExp(seqStr)}($|[|])`).test(currStr)) {
      return shortcut;
    }
  }

  return null;
};

export const isPrefixOfRegisteredSequence = (
  chord: string,
  registeredSequences: Map<string, SequenceShortcut>,
) => {
  for (const sequence of registeredSequences.values()) {
    if (sequence.shortcut.length > 1 && sequence.shortcut[0] === chord) {
      return true;
    }
  }

  return false;
};

export const endsWithSequence = (
  keys: SequenceShortcut['shortcut'],
  sequence: SequenceShortcut['shortcut'],
) => {
  const offset = keys.length - sequence.length;

  return offset >= 0 && sequence.every((key, index) => keys[offset + index] === key);
};

export const getComboShortcutIfMatch = (
  registeredCombos: Map<string, ComboShortcut>,
  comboToCheck: ComboShortcut['shortcut'],
) => {
  for (const [shortcut, combo] of registeredCombos.entries()) {
    const comboShortcut = combo.shortcut;

    if (comboToCheck.length < comboShortcut.length) {
      continue;
    }

    let match = true;
    let comboToCheckIndex = 0;

    for (let i = 0; i < comboShortcut.length; i++) {
      let found = false;

      for (let j = comboToCheckIndex; j < comboToCheck.length; j++) {
        if (comboShortcut[i].symmetricDifference(comboToCheck[j]).size === 0) {
          found = true;
          comboToCheckIndex = j + 1;
          break;
        }
      }

      if (!found) {
        match = false;
        break;
      }
    }

    if (match) {
      return shortcut;
    }
  }

  return null;
};
